'use client'

import { useState } from 'react'

import { Calendar, Clock, DollarSign, PawPrint, Scissors, Truck } from 'lucide-react'

import { useTodayCheckOuts } from '@/components/ReservationContext'
import { InvoiceModal } from '@/features/invoice/ui/InvoiceModal'
import { useReservationModal } from '@/pages/panel-interno/components/ReservationModalContext'
import { useHotelPricingConfig } from '@/shared/hooks/use-hotel-pricing-config'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardTitle } from '@/shared/ui/card'
import { type HotelReservation } from '@monorepo/functions/src/types/reservations'

export function CheckOuts() {
    const { reservations: checkOuts, isLoading, updateReservation } = useTodayCheckOuts()
    const [checkoutReservation, setCheckoutReservation] = useState<HotelReservation | null>(null)
    const { config: hotelPricingConfig } = useHotelPricingConfig()
    const { openReservationModal } = useReservationModal()

    const handleCheckOut = (id: string) => {
        const reservation = checkOuts.find(r => r.id === id)
        if (reservation) {
            setCheckoutReservation(reservation)
        }
    }

    const handleViewDetails = (id: string) => {
        const reservation = checkOuts.find(r => r.id === id)
        if (reservation) {
            openReservationModal(reservation)
        }
    }

    const handleCloseInvoice = () => {
        setCheckoutReservation(null)
    }

    const handlePaid = () => {
        if (checkoutReservation) {
            updateReservation(checkoutReservation.id, { isPaid: true })
            handleCloseInvoice()
        }
    }

    if (isLoading) {
        return <div className='py-8 text-center text-muted-foreground'>Cargando check-outs...</div>
    }

    if (checkOuts.length === 0) {
        return (
            <Alert variant='info' className='mt-2'>
                <Calendar className='h-4 w-4' />
                <AlertTitle>Sin check-outs</AlertTitle>
                <AlertDescription>No hay salidas programadas para el día de hoy.</AlertDescription>
            </Alert>
        )
    }

    return (
        <div className='space-y-4'>
            {checkOuts.map(checkOut => (
                <Card
                    key={checkOut.id}
                    className='cursor-pointer overflow-hidden transition-shadow hover:shadow-md'
                    onClick={() => handleViewDetails(checkOut.id)}
                >
                    <div className='flex'>
                        <div className='flex w-64 shrink-0 flex-col justify-between space-y-6 bg-[#4B6BFB]/5 p-6'>
                            <div className='space-y-6'>
                                <div>
                                    <CardTitle className='text-lg font-semibold'>{checkOut.client.name}</CardTitle>
                                    <div className='mt-1 space-y-1 text-sm text-muted-foreground'>
                                        <p>{checkOut.client.phone}</p>
                                        <p>{checkOut.client.email}</p>
                                    </div>
                                </div>

                                <div className='space-y-2'>
                                    <div className='flex items-center gap-2'>
                                        <Clock className='h-4 w-4' />
                                        <span className='text-sm text-muted-foreground'>Check-out: 12:00</span>
                                    </div>
                                    {checkOut.additionalServices.some(
                                        service =>
                                            service.type === 'driver' &&
                                            (service.serviceType === 'dropoff' || service.serviceType === 'both'),
                                    ) && (
                                        <div className='flex items-center gap-2'>
                                            <Truck className='h-4 w-4 text-[#4B6BFB]' />
                                            <span className='text-sm text-muted-foreground'>
                                                {checkOut.additionalServices.find(service => service.type === 'driver')
                                                    ?.serviceType === 'both'
                                                    ? 'Recogida y entrega'
                                                    : 'Entrega'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <CardContent className='flex-1 p-6'>
                            <div className='flex h-full flex-col'>
                                <div className='flex-grow space-y-6'>
                                    {/* Mascotas y sus servicios */}
                                    {checkOut.pets.map((pet, petIndex) => {
                                        // Filtrar servicios para esta mascota
                                        const petServices = checkOut.additionalServices.filter(
                                            service => service.type !== 'driver' && service.petIndex === petIndex, // Excluir servicio de transporte
                                        )

                                        return (
                                            <div key={petIndex} className='space-y-4 border-b pb-4 last:border-b-0'>
                                                <div className='grid grid-cols-[auto,1fr] gap-6'>
                                                    <div className='flex items-start gap-2'>
                                                        <PawPrint className='mt-1 h-4 w-4 text-[#4B6BFB]' />
                                                        <div>
                                                            <div className='flex items-center gap-2'>
                                                                <p className='font-medium'>{pet.name}</p>
                                                                <Badge
                                                                    variant='secondary'
                                                                    className='h-5 px-1.5 text-xs'
                                                                >
                                                                    {pet.roomNumber || 'Sin habitación'}
                                                                </Badge>
                                                            </div>
                                                            <p className='text-sm text-muted-foreground'>
                                                                {pet.breed} · {pet.size} · {pet.weight}kg ·{' '}
                                                                {pet.sex === 'M' ? 'Macho' : 'Hembra'} ·{' '}
                                                                {pet.isNeutered ? 'Castrado/a' : 'No castrado/a'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className='flex flex-wrap gap-4'>
                                                        {petServices.map((service, index) => {
                                                            let icon = null
                                                            let text = ''

                                                            switch (service.type) {
                                                                case 'hairdressing':
                                                                    icon = (
                                                                        <Scissors className='h-4 w-4 text-[#4B6BFB]' />
                                                                    )
                                                                    text = 'Peluquería: Baño y cepillado'
                                                                    break
                                                                case 'special_food':
                                                                    icon = (
                                                                        <DollarSign className='h-4 w-4 text-[#4B6BFB]' />
                                                                    )
                                                                    text = `Comida especial (${service.foodType === 'refrigerated' ? 'Refrigerada' : 'Congelada'})`
                                                                    break
                                                            }

                                                            return (
                                                                icon && (
                                                                    <div
                                                                        key={index}
                                                                        className='flex items-center gap-2'
                                                                    >
                                                                        {icon}
                                                                        <span className='text-sm text-muted-foreground'>
                                                                            {text}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Precio Total y Acciones */}
                                <div className='mt-4 space-y-4 border-t pt-4'>
                                    <div className='flex items-center justify-between'>
                                        <span className='font-semibold'>Precio Total</span>
                                        <span className='text-lg font-bold'>
                                            {(checkOut.totalPrice ?? 0).toLocaleString('es-ES', {
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 0,
                                            })}
                                            €
                                        </span>
                                    </div>
                                    <div className='flex items-center justify-end'>
                                        {checkOut.isPaid ? (
                                            <Badge variant='outline' className='ml-2'>
                                                Salida confirmada
                                            </Badge>
                                        ) : (
                                            <Button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    handleCheckOut(checkOut.id)
                                                }}
                                            >
                                                Cobrar y confirmar salida
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </div>
                </Card>
            ))}

            {checkoutReservation && (
                <InvoiceModal
                    reservation={checkoutReservation}
                    onClose={handleCloseInvoice}
                    onPaid={handlePaid}
                    config={hotelPricingConfig}
                />
            )}
        </div>
    )
}
